import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Eye, Download, Printer, Edit, Trash2, X, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import invoicesService from '../../../services/invoices.service'; // Import the Firebase service
import { jsPDF } from "jspdf";
import './AdminInvoices.css';
import { useToast } from '../../../context/ToastContext';

const AdminInvoices = () => {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const invoiceRef = useRef(null);

  // Form state for new/edit invoice
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    clientWilaya: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    taxRate: 19,
    discount: 0,
    notes: '',
    status: 'draft'
  });

  // Load invoices from Firebase
  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const result = await invoicesService.getAllInvoices();
      if (result.success) {
        setInvoices(result.invoices);
      } else {
        console.error('Error loading invoices:', result.error);
        toast.showError('Erreur lors du chargement des factures');
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.showError('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const calculateInvoiceTotals = (items, taxRate, discount) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = (afterDiscount * taxRate) / 100;
    const total = afterDiscount + taxAmount;

    return { subtotal, discountAmount, taxAmount, total };
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unitPrice: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedInvoice) {
        // Update existing invoice
        const result = await invoicesService.updateInvoice(selectedInvoice.id, formData);
        if (result.success) {
          await loadInvoices();
          setShowModal(false);
          resetForm();
          toast.showSuccess('Facture modifiée avec succès!');
        } else {
          toast.showError('Erreur: ' + result.message);
        }
      } else {
        // Create new invoice
        const result = await invoicesService.createInvoice(formData);
        if (result.success) {
          await loadInvoices();
          setShowModal(false);
          resetForm();
          toast.showSuccess('Facture créée avec succès!');
        } else {
          toast.showError('Erreur: ' + result.message);
        }
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.showError('Erreur lors de la sauvegarde de la facture');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!deleteConfirm) {
      setDeleteConfirm(invoiceId);
      return;
    }

    setLoading(true);
    try {
      const result = await invoicesService.deleteInvoice(invoiceId);
      if (result.success) {
        await loadInvoices();
        toast.showSuccess('Facture supprimée avec succès!');
      } else {
        toast.showError('Erreur: ' + result.message);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.showError('Erreur lors de la suppression');
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      clientName: invoice.clientName,
      clientEmail: invoice.clientEmail,
      clientPhone: invoice.clientPhone,
      clientAddress: invoice.clientAddress,
      clientWilaya: invoice.clientWilaya,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      items: invoice.items,
      taxRate: invoice.taxRate,
      discount: invoice.discount,
      notes: invoice.notes,
      status: invoice.status
    });
    setShowModal(true);
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreview(true);
  };

  const resetForm = () => {
    setSelectedInvoice(null);
    setFormData({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      clientAddress: '',
      clientWilaya: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      items: [{ description: '', quantity: 1, unitPrice: 0 }],
      taxRate: 19,
      discount: 0,
      notes: '',
      status: 'draft'
    });
  };

  const printInvoice = () => {
    const printContent = invoiceRef.current;
    const windowPrint = window.open('', '', 'width=800,height=1000');

    windowPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Facture ${selectedInvoice.invoiceNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            ${getInvoiceStyles()}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    windowPrint.document.close();
    windowPrint.focus();
    windowPrint.print();
    windowPrint.close();
  };

  const downloadPDF = async () => {
    try {
      const element = invoiceRef.current;
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '700px';
      tempContainer.innerHTML = element.innerHTML;

      const styles = document.createElement('style');
      styles.textContent = getInvoiceStyles();
      tempContainer.appendChild(styles);
      document.body.appendChild(tempContainer);

      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(tempContainer);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait"
      });
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`facture-${selectedInvoice.invoiceNumber}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.showError('Erreur lors de la génération du PDF');
    }
  };

  const getInvoiceStyles = () => {
    return `
      .invoice-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 40px;
        background: white;
        color: #1a202c;
        font-family: Arial, sans-serif;
      }
      .invoice-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 40px;
        padding-bottom: 20px;
        border-bottom: 3px solid #3b82f6;
      }
      .company-info h1 {
        font-size: 28px;
        color: #1a202c;
        margin: 0 0 8px 0;
        font-weight: 700;
      }
      .company-info p {
        color: #64748b;
        font-size: 14px;
        margin: 4px 0;
      }
      .invoice-meta {
        text-align: right;
      }
      .invoice-number {
        font-size: 24px;
        font-weight: bold;
        color: #3b82f6;
        margin-bottom: 8px;
      }
      .invoice-date {
        color: #64748b;
        font-size: 14px;
        margin: 4px 0;
      }
      .invoice-parties {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 30px;
        margin-bottom: 40px;
      }
      .party-box {
        padding: 20px;
        background: #f8fafc;
        border-radius: 8px;
        border-left: 4px solid #3b82f6;
      }
      .party-title {
        font-size: 14px;
        font-weight: bold;
        color: #64748b;
        text-transform: uppercase;
        margin-bottom: 12px;
        letter-spacing: 0.5px;
      }
      .party-details p {
        margin: 6px 0;
        color: #1a202c;
        font-size: 14px;
      }
      .party-details strong {
        font-weight: 600;
      }
      .invoice-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 30px;
      }
      .invoice-table thead {
        background: #3b82f6;
        color: white;
      }
      .invoice-table th {
        padding: 12px;
        text-align: left;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 600;
      }
      .invoice-table td {
        padding: 12px;
        border-bottom: 1px solid #e2e8f0;
        font-size: 14px;
        color: #1a202c;
      }
      .invoice-table tbody tr:hover {
        background: #f8fafc;
      }
      .text-right {
        text-align: right;
      }
      .invoice-summary {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 30px;
      }
      .summary-table {
        width: 350px;
      }
      .summary-table .summary-row {
        display: flex;
        justify-content: space-between;
        padding: 10px 0;
        border-bottom: 1px solid #e2e8f0;
        font-size: 14px;
      }
      .summary-table .summary-row span:first-child {
        color: #64748b;
      }
      .summary-table .summary-row span:last-child {
        font-weight: 600;
        color: #1a202c;
      }
      .summary-table .summary-row.total {
        background: #3b82f6;
        color: white;
        padding: 15px;
        border-radius: 8px;
        margin-top: 10px;
        font-size: 18px;
        font-weight: bold;
        border-bottom: none;
      }
      .summary-table .summary-row.total span {
        color: white;
      }
      .invoice-notes {
        padding: 20px;
        background: #f8fafc;
        border-radius: 8px;
        border-left: 4px solid #3b82f6;
        margin-bottom: 30px;
      }
      .invoice-notes h4 {
        font-size: 14px;
        color: #64748b;
        margin: 0 0 10px 0;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.5px;
      }
      .invoice-notes p {
        color: #1a202c;
        line-height: 1.6;
        font-size: 14px;
        margin: 0;
      }
      .invoice-footer {
        text-align: center;
        padding-top: 20px;
        border-top: 2px solid #e2e8f0;
        color: #64748b;
        font-size: 12px;
      }
      .invoice-footer p {
        margin: 4px 0;
      }
      .status-badge {
        display: inline-block;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .status-draft {
        background: #e2e8f0;
        color: #475569;
      }
      .status-sent {
        background: #dbeafe;
        color: #1e40af;
      }
      .status-paid {
        background: #d1fae5;
        color: #065f46;
      }
      .status-overdue {
        background: #fee2e2;
        color: #991b1b;
      }
    `;
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch =
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.clientName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid': return 'Payée';
      case 'sent': return 'Envoyée';
      case 'overdue': return 'En retard';
      default: return 'Brouillon';
    }
  };

  return (
    <div className="admin-invoices">
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Factures</h1>
          <p>{invoices.length} factures au total</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <Plus size={20} />
          Nouvelle Facture
        </button>
      </div>



      {/* Invoices Table */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Chargement...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="empty-container">
          <FileText size={64} className="empty-icon" />
          <h3 className="empty-title">Aucune facture trouvée</h3>
          <p className="empty-subtitle">Créez votre première facture pour commencer</p>
        </div>
      ) : (
        <div className="invoices-table-container">
          <table className="invoices-table">
            <thead>
              <tr>
                <th>N° Facture</th>
                <th>Client</th>
                <th>Date</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <span className="invoice-number2">{invoice.invoiceNumber}</span>
                  </td>
                  <td>
                    <div className="client-info">
                      <p className="client-name">{invoice.clientName}</p>
                      <p className="client-email">{invoice.clientEmail}</p>
                    </div>
                  </td>
                  <td className="invoice-date">
                    {new Date(invoice.invoiceDate).toLocaleDateString('fr-FR')}
                  </td>
                  <td>
                    <span className="invoice-amount">{invoice.total?.toLocaleString()} DA</span>
                  </td>
                  <td>
                    <span className={`status-badge ${invoice.status}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td>
                    <div className="actions-group">
                      <button
                        onClick={() => handleView(invoice)}
                        className="btn-action view"
                        title="Voir"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(invoice)}
                        className="btn-action edit"
                        title="Modifier"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className={`btn-action delete ${deleteConfirm === invoice.id ? 'confirm' : ''}`}
                        title={deleteConfirm === invoice.id ? 'Confirmer' : 'Supprimer'}
                      >
                        <Trash2 size={18} />
                      </button>
                      {deleteConfirm === invoice.id && (
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="btn-action cancel"
                          title="Annuler"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-title">
                {selectedInvoice ? 'Modifier la facture' : 'Nouvelle facture'}
              </h2>
              <button onClick={() => setShowModal(false)} className="btn-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Client Information */}
              <div className="form-section">
                <h3 className="form-section-title">Informations Client</h3>
                <div className="form-grid two-cols">
                  <div className="form-group">
                    <label className="form-label">Nom du client *</label>
                    <input
                      type="text"
                      required
                      value={formData.clientName}
                      onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      value={formData.clientEmail}
                      onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.clientPhone}
                      onChange={(e) => setFormData({ ...formData, clientPhone: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Wilaya</label>
                    <input
                      type="text"
                      value={formData.clientWilaya}
                      onChange={(e) => setFormData({ ...formData, clientWilaya: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label className="form-label">Adresse</label>
                    <input
                      type="text"
                      value={formData.clientAddress}
                      onChange={(e) => setFormData({ ...formData, clientAddress: e.target.value })}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="form-section">
                <h3 className="form-section-title">Détails de la facture</h3>
                <div className="form-grid three-cols">
                  <div className="form-group">
                    <label className="form-label">Date de facture *</label>
                    <input
                      type="date"
                      required
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date d'échéance</label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Statut *</label>
                    <select
                      required
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="form-select"
                    >
                      <option value="draft">Brouillon</option>
                      <option value="sent">Envoyée</option>
                      <option value="paid">Payée</option>
                      <option value="overdue">En retard</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="form-section">
                <div className="items-header">
                  <h3 className="form-section-title">Articles</h3>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="btn-add-item"
                  >
                    <Plus size={20} />
                    Ajouter un article
                  </button>
                </div>
                <div className="items-list">
                  {formData.items.map((item, index) => (
                    <div key={index} className="item-row">
                      <input
                        type="text"
                        placeholder="Description"
                        required
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="form-input item-description"
                      />
                      <input
                        type="number"
                        placeholder="Qté"
                        required
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        className="form-input item-quantity"
                      />
                      <input
                        type="number"
                        placeholder="Prix unitaire"
                        required
                        min="0"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="form-input item-price"
                      />
                      <div className="item-total">
                        {(item.quantity * item.unitPrice).toLocaleString()} DA
                      </div>
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="btn-remove-item"
                        >
                          <Trash2 size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tax & Discount */}
              <div className="form-section">
                <div className="form-grid two-cols">
                  <div className="form-group">
                    <label className="form-label">TVA (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.taxRate}
                      onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 0 })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Remise (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="form-section">
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes additionnelles..."
                    className="form-textarea"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="summary-box">
                <h3 className="summary-title">Résumé</h3>
                {(() => {
                  const totals = calculateInvoiceTotals(formData.items, formData.taxRate, formData.discount);
                  return (
                    <div className="summary-rows">
                      <div className="summary-row">
                        <span className="label">Sous-total:</span>
                        <span className="value">{totals.subtotal.toLocaleString()} DA</span>
                      </div>
                      {formData.discount > 0 && (
                        <div className="summary-row discount">
                          <span className="label">Remise ({formData.discount}%):</span>
                          <span className="value">-{totals.discountAmount.toLocaleString()} DA</span>
                        </div>
                      )}
                      <div className="summary-row">
                        <span className="label">TVA ({formData.taxRate}%):</span>
                        <span className="value">{totals.taxAmount.toLocaleString()} DA</span>
                      </div>
                      <div className="summary-row total">
                        <span>Total:</span>
                        <span>{totals.total.toLocaleString()} DA</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-submit"
                >
                  {loading ? 'Enregistrement...' : selectedInvoice ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoicePreview && selectedInvoice && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h2 className="modal-title">Aperçu de la facture</h2>
              <div className="preview-header">
                <button
                  onClick={printInvoice}
                  className="btn-print"
                >
                  <Printer size={20} />
                  Imprimer
                </button>
                <button
                  onClick={downloadPDF}
                  className="btn-download"
                >
                  <Download size={20} />
                  PDF
                </button>
                <button
                  onClick={() => setShowInvoicePreview(false)}
                  className="btn-close"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="preview-content">
              <div ref={invoiceRef} className="invoice-container">
                {/* Invoice Header */}
                <div className="invoice-header">
                  <div className="company-info">
                    <h1>Burotime</h1>
                    <p>+213 552 49 33 48</p>
                    <p>burotime@gmail.com</p>
                    <p>Alger, Algeria</p>
                  </div>
                  <div className="invoice-meta">
                    <div className="invoice-number">{selectedInvoice.invoiceNumber}</div>
                    <div className="invoice-date">
                      Date: {new Date(selectedInvoice.invoiceDate).toLocaleDateString('fr-FR')}
                    </div>
                    {selectedInvoice.dueDate && (
                      <div className="invoice-date">
                        Échéance: {new Date(selectedInvoice.dueDate).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                    <div style={{ marginTop: '10px' }}>
                      <span className={`status-badge status-${selectedInvoice.status}`}>
                        {getStatusLabel(selectedInvoice.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Parties */}
                <div className="invoice-parties">
                  <div className="party-box">
                    <div className="party-title">Facturé par</div>
                    <div className="party-details">
                      <p><strong>Burotime</strong></p>
                      <p>Alger, Algeria</p>
                      <p>Tél: +213 552 49 33 48</p>
                      <p>Email: Burotime@gmail.com</p>
                    </div>
                  </div>
                  <div className="party-box">
                    <div className="party-title">Facturé à</div>
                    <div className="party-details">
                      <p><strong>{selectedInvoice.clientName}</strong></p>
                      {selectedInvoice.clientAddress && <p>{selectedInvoice.clientAddress}</p>}
                      {selectedInvoice.clientWilaya && <p>{selectedInvoice.clientWilaya}</p>}
                      {selectedInvoice.clientPhone && <p>Tél: {selectedInvoice.clientPhone}</p>}
                      {selectedInvoice.clientEmail && <p>Email: {selectedInvoice.clientEmail}</p>}
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50%' }}>Description</th>
                      <th className="text-right">Quantité</th>
                      <th className="text-right">Prix unitaire</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.description}</td>
                        <td className="text-right">{item.quantity}</td>
                        <td className="text-right">{item.unitPrice.toLocaleString()} DA</td>
                        <td className="text-right">{(item.quantity * item.unitPrice).toLocaleString()} DA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Summary */}
                <div className="invoice-summary">
                  <div className="summary-table">
                    <div className="summary-row">
                      <span>Sous-total:</span>
                      <span>{selectedInvoice.subtotal?.toLocaleString()} DA</span>
                    </div>
                    {selectedInvoice.discount > 0 && (
                      <div className="summary-row" style={{ color: '#059669' }}>
                        <span>Remise ({selectedInvoice.discount}%):</span>
                        <span>-{selectedInvoice.discountAmount?.toLocaleString()} DA</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span>TVA ({selectedInvoice.taxRate}%):</span>
                      <span>{selectedInvoice.taxAmount?.toLocaleString()} DA</span>
                    </div>
                    <div className="summary-row total">
                      <span>Total à payer:</span>
                      <span>{selectedInvoice.total?.toLocaleString()} DA</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedInvoice.notes && (
                  <div className="invoice-notes">
                    <h4>Notes</h4>
                    <p>{selectedInvoice.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="invoice-footer">
                  <p>Merci pour votre confiance!</p>
                  <p>Cette facture a été générée électroniquement et est valable sans signature.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;